/**
 * The HTTP client every page uses, and the access-token lifecycle that sits inside it.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THE ACCESS TOKEN IS IN A MODULE VARIABLE AND NOT IN localStorage
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The refresh token is an httpOnly, SameSite=Strict cookie — the API sets it that way
 * deliberately (`api/src/auth/sessions.ts`), which means JavaScript cannot read it and no
 * cross-site navigation sends it. That protection is worth exactly nothing if the ACCESS
 * token is then parked in `localStorage`, where any script on the origin can read it: an
 * attacker with script execution would no longer need the cookie, because a 15-minute bearer
 * token is enough to do anything the user can do, and it can be exfiltrated in one request.
 *
 * So the access token lives in a closure for the life of the tab and nowhere else. The cost
 * is one extra request on a cold load — `POST /auth/refresh`, which the cookie authenticates
 * — and that is what `bootstrapSession()` is. The benefit is that a page reload is the ONLY
 * way to lose it, and there is nothing at rest for a future XSS to find.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * AND WHY THE REFRESH IS SINGLE-FLIGHT
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This is not a performance optimisation. `api/src/auth/sessions.ts` implements refresh-token
 * ROTATION WITH REUSE DETECTION: consuming a refresh token issues a new one and invalidates
 * the old, and presenting an already-spent token is treated as theft and revokes the entire
 * token family.
 *
 * A dashboard mounts several queries at once. If the access token has expired, every one of
 * them 401s at the same instant, and a naive "on 401, refresh and retry" fires N concurrent
 * refreshes with the SAME cookie. The first rotates it; the rest present a spent token; the
 * API correctly concludes the family is compromised and logs the user out — of every session
 * they have. The user sees a random logout on a page that was working a second ago.
 *
 * `refreshInFlight` makes the second through Nth callers await the first one's promise.
 */

export interface SessionUser {
  id: string;
  email: string;
  displayName: string | null;
  role: 'superadmin' | 'admin' | 'supervisor' | 'agent';
  tenantId: string;
  mfaEnabled: boolean;
}

export interface SessionResponse {
  accessToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
  user: SessionUser;
}

/** The shape `api/src/http/errors.ts` always returns. */
export interface ApiErrorBody {
  error: { code: string; message: string; requestId: string; details?: { path: string; message: string }[] };
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | undefined,
    readonly details?: { path: string; message: string }[],
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** `mfa_required` is a 428 and is a prompt, not a failure. See `routes/auth/login.ts`. */
  get needsMfa(): boolean {
    return this.status === 428 && this.code === 'mfa_required';
  }
}

// ── the in-memory session ──────────────────────────────────────────────────

let accessToken: string | undefined;
let currentUser: SessionUser | undefined;
let refreshInFlight: Promise<SessionResponse | undefined> | undefined;

type Listener = (user: SessionUser | undefined) => void;
const listeners = new Set<Listener>();

/** Subscribe to session changes. Returns the unsubscribe function. */
export function onSessionChange(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function setSession(session: SessionResponse | undefined): void {
  accessToken = session?.accessToken;
  currentUser = session?.user;
  for (const listener of listeners) listener(currentUser);
}

export const getUser = (): SessionUser | undefined => currentUser;

/**
 * Exposed for the softphone, which needs a bearer token for `GET /turn/credentials` and for
 * the `/events` WebSocket — a browser cannot set an `Authorization` header on a WebSocket, so
 * `routes/events.ts` takes the token in the first message instead.
 */
export const getAccessToken = (): string | undefined => accessToken;

// ── the fetch wrapper ──────────────────────────────────────────────────────

/**
 * Exported because the softphone diagnostic (`lib/diagnoseSoftphone.ts`) probes `/healthz` and
 * `/system/health` with a bare `fetch` rather than through `request()` — it has to SEE a network
 * failure as a network failure, and `request()`'s job is to turn those into `ApiError`s and
 * refresh sessions around them. A second hardcoded `'/api'` is a place for the two to drift.
 */
export const BASE = '/api';

async function parse(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined;
  const text = await response.text();
  if (text === '') return undefined;
  try {
    return JSON.parse(text);
  } catch {
    /**
     * Not JSON. This is nginx or Traefik answering, not the API — a 502 while `pbx-api` is
     * restarting, or a 413 from `client_max_body_size`. Returning the raw text as a message
     * is what makes that distinguishable from an application error, which always has a
     * `requestId`.
     */
    return { error: { code: 'non_json_response', message: text.slice(0, 500), requestId: '' } };
  }
}

function toError(status: number, body: unknown): ApiError {
  const parsed = body as Partial<ApiErrorBody>;
  const error = parsed?.error;
  return new ApiError(
    status,
    error?.code ?? 'unknown',
    error?.message ?? `request failed with ${status}`,
    error?.requestId === '' ? undefined : error?.requestId,
    error?.details,
  );
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
  /**
   * Internal. Stops the 401 handler from recursing: the retry after a refresh must not itself
   * trigger another refresh, or an endpoint that 401s for a reason OTHER than expiry (a
   * deleted user, a revoked session) becomes an infinite loop of refreshes.
   */
  retrying?: boolean;
  /**
   * `'blob'` returns the body as bytes, for audio. An `<audio src>` cannot send the bearer
   * header, so anything the console PLAYS comes through here and out as an object URL.
   */
  responseType?: 'json' | 'blob';
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, retrying = false, responseType = 'json' } = options;

  /**
   * ⚠️ A `FormData` body must go out UNTOUCHED and WITHOUT a content-type. The browser writes
   * `multipart/form-data; boundary=…` itself, and the boundary is the part that matters — set
   * the header by hand and the server cannot find where the file starts. Stringified, it
   * becomes `{}`.
   */
  const multipart = body instanceof FormData;

  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  if (body !== undefined && !multipart) headers['content-type'] = 'application/json';

  const response = await fetch(`${BASE}${path}`, {
    method,
    headers,
    // Always: the refresh cookie is httpOnly and same-origin, and omitting this silently
    // breaks login and logout while leaving every other call working.
    credentials: 'same-origin',
    ...(body === undefined ? {} : { body: multipart ? body : JSON.stringify(body) }),
    ...(signal ? { signal } : {}),
  });

  if (response.ok) return (responseType === 'blob' ? await response.blob() : await parse(response)) as T;

  /**
   * ── ONE RETRY, AND ONLY FOR AN EXPIRED ACCESS TOKEN ──────────────────────
   * A 401 here means the token was rejected. It is usually expiry — the token lives 15
   * minutes and a console tab lives all day — so a refresh and one retry is the right
   * response and is invisible to the user. If the refresh also fails, the session is
   * genuinely over and `setSession(undefined)` inside `refreshSession` has already told the
   * router, which redirects to the login page.
   */
  if (response.status === 401 && !retrying) {
    const refreshed = await refreshSession();
    if (refreshed) return request<T>(path, { ...options, retrying: true });
  }

  throw toError(response.status, await parse(response));
}

// ── the session lifecycle ──────────────────────────────────────────────────

/**
 * Exchange the refresh cookie for a new access token. Single-flight — see the header.
 *
 * Returns `undefined` rather than throwing when there is no valid session: on a cold load
 * with no cookie this is the EXPECTED path, not an error, and making the caller catch an
 * exception for the ordinary "not logged in yet" case is how a real failure ends up in the
 * same `catch` as a normal one.
 */
export async function refreshSession(): Promise<SessionResponse | undefined> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async (): Promise<SessionResponse | undefined> => {
    try {
      const response = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!response.ok) {
        setSession(undefined);
        return undefined;
      }
      const session = (await parse(response)) as SessionResponse;
      setSession(session);
      return session;
    } catch {
      // A network failure is not proof the session is dead — the laptop may have been asleep.
      // But there is no token to carry on with either, so the caller is told it did not work
      // and the session is cleared; the login page is a truthful thing to show.
      setSession(undefined);
      return undefined;
    } finally {
      // Cleared in `finally` so a failed refresh does not leave every future call awaiting a
      // promise that will never be retried.
      refreshInFlight = undefined;
    }
  })();

  return refreshInFlight;
}

export interface LoginInput {
  email: string;
  password: string;
  totp?: string;
}

export async function login(input: LoginInput): Promise<SessionResponse> {
  const response = await fetch(`${BASE}/auth/login`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });

  const body = await parse(response);
  if (!response.ok) throw toError(response.status, body);

  const session = body as SessionResponse;
  setSession(session);
  return session;
}

export async function logout(everywhere = false): Promise<void> {
  /**
   * ── `catch`, NOT `finally`, AND THE DIFFERENCE IS THE WHOLE POINT ─────────
   * This was `try/finally` first, and its own test caught it. A `finally` block clears the
   * session and then RE-THROWS, so on a network error the caller's
   * `await logout(); navigate('/login')` never reaches the navigate: the user is signed out
   * in memory and still looking at the console, with the sign-out button apparently doing
   * nothing. Swallowing is correct here specifically because there is no failure mode worth
   * reporting — `POST /auth/logout` already answers 200 with no cookie, an expired one or a
   * revoked one, so the only errors that reach this line are transport ones, and the local
   * state has already been cleared regardless.
   */
  try {
    await fetch(`${BASE}/auth/logout`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ everywhere }),
    });
  } catch {
    // See above. The server-side session may outlive this, and it expires on its own clock.
  }
  setSession(undefined);
}

/**
 * Called once, before the router renders. Resolves to the user if the refresh cookie is still
 * good, and to `undefined` otherwise — which is what makes a reload keep you logged in.
 */
export async function bootstrapSession(): Promise<SessionUser | undefined> {
  const session = await refreshSession();
  return session?.user;
}

// ── how one may sign in ────────────────────────────────────────────────────

export interface AuthProviders {
  /** Always true: the local login never depends on the identity provider (`api/src/auth/oidc.ts`). */
  password: true;
  oidc: { enabled: boolean; displayName: string };
}

const PASSWORD_ONLY: AuthProviders = { password: true, oidc: { enabled: false, displayName: '' } };

/**
 * `GET /auth/providers`, for the login page. Any failure — the API down, an older API without
 * the route, a network error — is "no single sign-on", never "no login": the password form must
 * not depend on this answer, and it does not wait for it either.
 */
export async function getAuthProviders(): Promise<AuthProviders> {
  try {
    const response = await fetch(`${BASE}/auth/providers`, { credentials: 'same-origin' });
    if (!response.ok) return PASSWORD_ONLY;
    const body = (await parse(response)) as Partial<AuthProviders> | undefined;
    const oidc = body?.oidc;
    if (!oidc || typeof oidc.enabled !== 'boolean') return PASSWORD_ONLY;
    return { password: true, oidc: { enabled: oidc.enabled, displayName: typeof oidc.displayName === 'string' ? oidc.displayName : '' } };
  } catch {
    return PASSWORD_ONLY;
  }
}

/**
 * Where the "Sign in with …" link goes. A real navigation, not a fetch: the API answers 302 to
 * the identity provider, which the browser must follow as a page. `redirectTo` is where the
 * console opens afterwards; the API keeps only a path on this origin (`safeRedirect`).
 */
export const oidcStartUrl = (redirectTo: string): string =>
  `${BASE}/auth/oidc/start?redirect=${encodeURIComponent(redirectTo)}`;

// ── connecting an existing account to the identity provider ────────────────

/** What `GET /auth/oidc/link` says is waiting: the account the provider's e-mail matched. */
export interface PendingOidcLink {
  provider: string;
  /** The provider's identity, not the console account's — nothing is proved yet. */
  email: string;
  displayName: string | null;
  redirectTo: string;
}

/**
 * The identity parked by the callback for the "connect your account" step, or `undefined` when
 * there is none (expired, already used, or the page was opened by hand). Not an error either way.
 */
export async function getPendingOidcLink(): Promise<PendingOidcLink | undefined> {
  try {
    const response = await fetch(`${BASE}/auth/oidc/link`, { credentials: 'same-origin' });
    if (!response.ok) return undefined;
    return (await parse(response)) as PendingOidcLink;
  } catch {
    return undefined;
  }
}

/**
 * Prove the matched account with its console password (and code), which binds the provider
 * identity to it and signs the person in — the same `SessionResponse` as `login()`, and the same
 * 428 `mfa_required` step. The session is set here exactly as `login()` sets it.
 */
export async function completeOidcLink(input: { password: string; totp?: string }): Promise<SessionResponse> {
  const response = await fetch(`${BASE}/auth/oidc/link`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(input),
  });
  const body = await parse(response);
  if (!response.ok) throw toError(response.status, body);
  const session = body as SessionResponse;
  setSession(session);
  return session;
}
